import { useMemo, useRef, useEffect, useState, useCallback, forwardRef } from 'react';
import { format, setHours, setMinutes, isBefore } from 'date-fns';
import { toZonedTime, fromZonedTime, formatInTimeZone } from 'date-fns-tz';
import { CalendarX, ChevronUp, ChevronDown } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import type { DaySlotsResponse, ProviderDaySlots, DaySlot } from '@vagusx/clients';
import { useProviderColors, type ProviderColor } from './hooks/use-provider-colors';
import { SlotHoverCard } from './SlotHoverCard';

/**
 * Each column renders its slots in that column's OWN location timezone, so a
 * clinic's 9:00 slot sits on the 9 AM row whatever the viewer's timezone — and
 * a multi-state practice (NY / TX / HI in one view) shows each clinic's real
 * local times. The left time gutter is therefore a generic clock (the "9 AM"
 * row means 9 AM local at whichever clinic the column belongs to), so two
 * clinics' 9:00 slots share a row even though they're different instants. Each
 * column header carries its own tz badge to disambiguate.
 */
function browserTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

/** Short tz abbreviation (e.g. "EDT") for the header, in the given zone. */
function tzAbbrev(tz: string): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      timeZoneName: 'short',
    }).formatToParts(new Date());
    return parts.find((p) => p.type === 'timeZoneName')?.value ?? tz;
  } catch {
    return tz;
  }
}

// Timeline configuration — the visible window is fit to the day's data at runtime,
// clamped to this default range when there are no slots.
const DEFAULT_START_HOUR = 7; // 7 AM
const DEFAULT_END_HOUR = 19; // 7 PM
// 5-min grid resolution so short slots (e.g. a 5-min athena slot) render at their
// true position/height. Height per row keeps an hour at 144px (12 rows × 12px),
// identical to the old 15-min grid, so the overall calendar height is unchanged.
const SLOT_INTERVAL_MINUTES = 5;
const SLOT_HEIGHT_PX = 12;
const TIME_COLUMN_WIDTH = 80;
const PROVIDER_COLUMN_WIDTH = 180;
// A column's width: PROVIDER_COLUMN_WIDTH (180px) at least, growing into spare
// width up to 320px, so a few columns don't leave the screen empty and one
// column doesn't stretch across it. Shared by header and body so they align.
const COLUMN_WIDTH_CLASSES = 'flex-1 min-w-45 max-w-80';
const HEADER_HEIGHT = 56; // Provider header row height

type ColumnLocation = ProviderDaySlots['location'];

/** Whether `date` (a local-midnight day) is today at a clinic in `timezone`. */
function isClinicToday(date: Date, timezone: string): boolean {
  return format(date, 'yyyy-MM-dd') === formatInTimeZone(new Date(), timezone, 'yyyy-MM-dd');
}

/** A column's display timezone: its location's zone, or the viewer's as fallback. */
export function columnTimeZone(location: ColumnLocation): string {
  return location?.timezone ?? browserTimeZone();
}

/**
 * The one timezone shared by every column, or null when columns span more than
 * one (or there are none). Used to decide whether a single unambiguous "now"
 * row exists for the scroll-to-current-time behavior.
 */
export function sharedColumnTimeZone(providers: readonly ProviderDaySlots[]): string | null {
  const zones = providers.map((p) => columnTimeZone(p.location));
  const tz = zones[0];
  if (!tz || zones.some((z) => z !== tz)) return null;
  return tz;
}

/**
 * Fit the shared [startHour, endHour) window to the union of every slot's
 * local-clock position — each slot measured in ITS OWN column timezone, so a
 * grid spanning timezones still shows every slot. Empty days fall back to the
 * default window; populated days clamp to [0, 24].
 */
export function computeWindow(providers: readonly ProviderDaySlots[]): [number, number] {
  let minMin = Infinity;
  let maxMin = -Infinity;
  for (const p of providers) {
    const tz = columnTimeZone(p.location);
    for (const s of p.slots) {
      const z = toZonedTime(s.startTime, tz);
      const startMin = z.getHours() * 60 + z.getMinutes();
      minMin = Math.min(minMin, startMin);
      maxMin = Math.max(maxMin, startMin + s.durationMinutes);
    }
  }
  if (minMin === Infinity) return [DEFAULT_START_HOUR, DEFAULT_END_HOUR];
  return [Math.max(0, Math.floor(minMin / 60)), Math.min(24, Math.ceil(maxMin / 60))];
}

/** A proposed booking drawn over the grid (the reschedule calendar's pick). */
export interface SlotsPreview {
  providerId: string;
  locationId: string | null;
  /** ISO instant. */
  startTime: string;
  durationMinutes: number;
  label: string;
  /**
   * The open slot that was picked, if one was: the preview then sits in that
   * slot's lane, not across the column over the slots beside it. An empty-time
   * pick has none and spans the column.
   */
  slotId?: string;
}

/** One day of the grid: the day, and its provider@location columns. */
export interface SlotsDay {
  date: Date;
  data: DaySlotsResponse;
}

interface SlotsViewProps {
  /**
   * The days to show, in order. One day is the practice's day view (a column per
   * provider@location). Several days put each day's columns side by side, so one
   * provider's week reads left to right. A day with no availability and no
   * bookings has no columns and so takes no space.
   */
  days: readonly SlotsDay[] | undefined;
  isLoading?: boolean;
  /**
   * Patient-name filter. The slots grid is provider×time, so open slots (no
   * patient) can't be filtered out — instead a non-empty term highlights booked
   * appointments whose patient matches and dims everything else, keeping the
   * full grid (and open slots) visible for booking.
   */
  searchTerm?: string;
  onSlotClick?: (slot: DaySlot, providerId: string, location: ColumnLocation) => void;
  /** A booked appointment to single out (the one being rescheduled). */
  currentAppointmentId?: string;
  /** A block drawn at a picked time, sized to the booking it would make. */
  preview?: SlotsPreview | null;
  /** Leave the date out of the summary bar, when the page already shows it. */
  hideDateLabel?: boolean;
  /**
   * Open slots the pick can't take (the reschedule calendar: the slot is only for
   * other visit types, ADR-313). Drawn muted as "Other type"; still hoverable.
   */
  refusesSlot?: (slot: DaySlot) => boolean;
}

/** True when a booked slot's patient name matches the (already-lowercased) term. */
function slotMatchesSearch(slot: DaySlot, lowerTerm: string): boolean {
  if (!lowerTerm) return true;
  if (slot.status !== 'booked' || !slot.appointment) return false;
  return (slot.appointment.patient?.name ?? '').toLowerCase().includes(lowerTerm);
}

/** Stable key for a provider@location column (a provider may appear at several locations). */
function columnKey(providerData: ProviderDaySlots): string {
  return `${providerData.provider.id}|${providerData.location?.id ?? ''}`;
}

/** A rendered column: one provider at one location on one day. */
interface GridColumn {
  key: string;
  date: Date;
  providerData: ProviderDaySlots;
}

/** Every day's columns, day by day, in the order the grid draws them. */
export function gridColumns(days: readonly SlotsDay[]): GridColumn[] {
  return days.flatMap((day) =>
    day.data.providers.map((providerData) => ({
      key: `${format(day.date, 'yyyy-MM-dd')}|${columnKey(providerData)}`,
      date: day.date,
      providerData,
    }))
  );
}

/** "Wednesday, September 30, 2026", or "Wed, Sep 30 – Tue, Oct 6" for several days. */
function daysLabel(days: readonly SlotsDay[]): string {
  const first = days[0];
  const last = days[days.length - 1];
  if (!first || !last) return '';
  if (days.length === 1) return format(first.date, 'EEEE, MMMM d, yyyy');
  return `${format(first.date, 'EEE, MMM d')} – ${format(last.date, 'EEE, MMM d')}`;
}

function plural(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}

export function SlotsView({
  days,
  isLoading,
  searchTerm,
  onSlotClick,
  currentAppointmentId,
  preview,
  hideDateLabel,
  refusesSlot,
}: SlotsViewProps): React.ReactElement {
  const lowerTerm = searchTerm?.trim().toLowerCase() ?? '';
  const multiDay = (days?.length ?? 0) > 1;

  const columns = useMemo(() => gridColumns(days ?? []), [days]);
  const columnProviders = useMemo(() => columns.map((c) => c.providerData), [columns]);

  // Fit the timeline to the data so every slot renders — nothing is dropped
  // off-grid, and the header counts match what's drawn. The window unions every
  // column's local-clock range (each measured in its own timezone), so a
  // multi-timezone grid still fits. Empty grids fall back to the default window.
  const [startHour, endHour] = useMemo<[number, number]>(
    () => computeWindow(columnProviders),
    [columnProviders]
  );

  // Generate the time-axis rows at the grid resolution.
  const timeSlots = useMemo(() => {
    const slots: { hour: number; minute: number; label: string }[] = [];
    for (let hour = startHour; hour < endHour; hour++) {
      for (let minute = 0; minute < 60; minute += SLOT_INTERVAL_MINUTES) {
        slots.push({
          hour,
          minute,
          label: format(setMinutes(setHours(new Date(), hour), minute), 'h:mm a'),
        });
      }
    }
    return slots;
  }, [startHour, endHour]);

  // Map providers for color assignment (each provider once, however many days).
  const providerList = useMemo(() => {
    const byId = new Map<string, { id: string; firstName: string; lastName: string }>();
    for (const p of columnProviders) {
      byId.set(p.provider.id, {
        id: p.provider.id,
        firstName: p.provider.firstName,
        lastName: p.provider.lastName,
      });
    }
    return [...byId.values()];
  }, [columnProviders]);

  const providerColors = useProviderColors(providerList);

  const summary = useMemo(() => {
    let totalOpen = 0;
    let totalBooked = 0;
    for (const day of days ?? []) {
      totalOpen += day.data.summary.totalOpen;
      totalBooked += day.data.summary.totalBooked;
    }
    return { totalOpen, totalBooked, providerCount: providerList.length };
  }, [days, providerList.length]);

  // Resolve the patient search to the ordered list of matching slots — top-to-bottom
  // by time, then by column — so the prev/next controls step through them in the
  // same order they read on the grid. The derived Set gives columns O(1) highlight/
  // dim lookups, and the list length is the summary-bar count.
  const matchingSlots = useMemo(() => {
    const matches: { id: string; startTime: string; colIndex: number }[] = [];
    if (!lowerTerm) return matches;
    columnProviders.forEach((p, colIndex) => {
      for (const s of p.slots) {
        if (slotMatchesSearch(s, lowerTerm)) {
          matches.push({ id: s.id, startTime: s.startTime, colIndex });
        }
      }
    });
    matches.sort((a, b) => a.startTime.localeCompare(b.startTime) || a.colIndex - b.colIndex);
    return matches;
  }, [columnProviders, lowerTerm]);
  const matchingSlotIds = useMemo(() => new Set(matchingSlots.map((m) => m.id)), [matchingSlots]);
  const matchCount = matchingSlots.length;

  // Prev/next navigation through the matches. The index resets to the first match
  // whenever the search term changes, and is clamped on read since a refetch can
  // shrink the match list out from under the stored index.
  const [activeMatchIndex, setActiveMatchIndex] = useState(0);
  useEffect(() => {
    setActiveMatchIndex(0);
  }, [lowerTerm]);
  const safeIndex = matchCount > 0 ? Math.min(activeMatchIndex, matchCount - 1) : 0;
  const activeSlotId = matchingSlots[safeIndex]?.id ?? null;

  // The single open hover card, owned here so only one shows at a time. Hover
  // opens a slot's card (closing whatever was open); navigating the search
  // matches previews the active one by opening it directly.
  const [openCardId, setOpenCardId] = useState<string | null>(null);
  useEffect(() => {
    setOpenCardId(activeSlotId);
  }, [activeSlotId]);
  const onCardOpenChange = useCallback((slotId: string, isOpen: boolean) => {
    // Opening claims the single slot; closing only clears if this slot still owns it
    // (a newer card may have opened first as the pointer moved between cells).
    setOpenCardId((prev) => (isOpen ? slotId : prev === slotId ? null : prev));
  }, []);
  const goToMatch = useCallback(
    (next: number) => {
      if (matchCount === 0) return;
      setActiveMatchIndex(((next % matchCount) + matchCount) % matchCount); // wrap-around
    },
    [matchCount]
  );

  // Refs to the matched slot blocks so navigating can scroll the active one into
  // view. Only matched slots register, so the map stays small.
  const slotRefs = useRef(new Map<string, HTMLDivElement>());
  const registerSlotRef = useCallback((id: string, el: HTMLDivElement | null) => {
    if (el) slotRefs.current.set(id, el);
    else slotRefs.current.delete(id);
  }, []);

  // Auto-scroll to current time when today is on screen. Also mirrored into state so
  // the hover card can use the scroll container as its collision boundary — a
  // bare ref wouldn't re-render Radix's Portal content when the element mounts.
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [scrollEl, setScrollEl] = useState<HTMLDivElement | null>(null);
  const setScrollContainer = useCallback((el: HTMLDivElement | null) => {
    scrollContainerRef.current = el;
    setScrollEl(el);
  }, []);
  useEffect(() => {
    if (columns.length === 0 || !scrollContainerRef.current) return;
    if (!columns.some((c) => isClinicToday(c.date, columnTimeZone(c.providerData.location)))) {
      return;
    }

    // Each column renders in its own clinic timezone, so a single instant maps to a
    // different row per column — there's no one "now" row across mixed zones. Only
    // auto-scroll when every column shares one timezone; then that clinic clock gives
    // an unambiguous, viewer-independent "now". With mixed zones, leave the scroll at
    // the top (business-hours start) rather than guess.
    const tz = sharedColumnTimeZone(columnProviders);
    if (!tz) return;

    const nowZoned = toZonedTime(new Date(), tz);
    const currentHour = nowZoned.getHours();
    const currentMinute = nowZoned.getMinutes();

    // Calculate which slot index the current time falls into
    const minutesSinceStart = (currentHour - startHour) * 60 + currentMinute;
    if (minutesSinceStart < 0) return; // Before timeline start

    const slotIndex = Math.floor(minutesSinceStart / SLOT_INTERVAL_MINUTES);
    // Scroll to 1 slot before current time for context, account for sticky header
    const scrollTarget = Math.max(0, slotIndex * SLOT_HEIGHT_PX - SLOT_HEIGHT_PX - HEADER_HEIGHT);
    scrollContainerRef.current.scrollTop = scrollTarget;
  }, [columns, columnProviders, startHour]);

  // Bring the active match into view when navigating (and when a search first
  // resolves). Runs after the current-time effect so a search wins the scroll on
  // load. Centering in the viewport sidesteps the sticky header / sticky time-
  // column overlap a top/left-aligned scroll would hit.
  useEffect(() => {
    if (!activeSlotId) return;
    const el = slotRefs.current.get(activeSlotId);
    const container = scrollContainerRef.current;
    if (!el || !container) return;
    const cRect = container.getBoundingClientRect();
    const eRect = el.getBoundingClientRect();
    const top =
      eRect.top - cRect.top + container.scrollTop - container.clientHeight / 2 + eRect.height / 2;
    const left =
      eRect.left - cRect.left + container.scrollLeft - container.clientWidth / 2 + eRect.width / 2;
    container.scrollTo({ top: Math.max(0, top), left: Math.max(0, left), behavior: 'smooth' });
  }, [activeSlotId]);

  if (isLoading) {
    return <SlotsViewSkeleton />;
  }

  if (columns.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground py-16">
        <CalendarX className="h-10 w-10 mb-3 opacity-40" />
        <p className="text-sm font-medium">No slots available</p>
        <p className="text-xs opacity-70">
          {multiDay
            ? 'No providers have slots on these days'
            : 'No providers have slots for this day'}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Summary Bar */}
      <div className="shrink-0 px-4 py-2 border-b border-border/50 bg-muted/30">
        <div className="flex items-center gap-4 text-sm">
          {!hideDateLabel && <span className="font-medium">{daysLabel(days ?? [])}</span>}
          <span className="text-muted-foreground">
            <span className="text-emerald-600 dark:text-emerald-400 font-medium">
              {summary.totalOpen} open
            </span>
            {' • '}
            <span>{summary.totalBooked} booked</span>
            {' • '}
            <span>{plural(summary.providerCount, 'provider', 'providers')}</span>
          </span>
          {lowerTerm && (
            <div className="flex items-center gap-1.5">
              <span className="rounded bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                {matchCount} matching &ldquo;{searchTerm?.trim()}&rdquo;
              </span>
              {matchCount > 0 && (
                <div className="flex items-center gap-0.5 text-xs text-muted-foreground">
                  <button
                    type="button"
                    aria-label="Previous match"
                    disabled={matchCount <= 1}
                    onClick={() => goToMatch(safeIndex - 1)}
                    className="rounded p-0.5 hover:bg-muted hover:text-foreground focus:outline-hidden focus:ring-2 focus:ring-amber-500/50 disabled:opacity-40 disabled:hover:bg-transparent"
                  >
                    <ChevronUp className="h-3.5 w-3.5" />
                  </button>
                  <span className="tabular-nums">
                    {safeIndex + 1} / {matchCount}
                  </span>
                  <button
                    type="button"
                    aria-label="Next match"
                    disabled={matchCount <= 1}
                    onClick={() => goToMatch(safeIndex + 1)}
                    className="rounded p-0.5 hover:bg-muted hover:text-foreground focus:outline-hidden focus:ring-2 focus:ring-amber-500/50 disabled:opacity-40 disabled:hover:bg-transparent"
                  >
                    <ChevronDown className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}
            </div>
          )}
          <span className="ml-auto text-xs text-muted-foreground">
            Each column in its clinic&rsquo;s local time
          </span>
        </div>
      </div>

      {/* Scrollable Timeline Grid (both axes) — plain overflow-auto, not ScrollArea */}
      <div className="flex-1 min-h-0 relative">
        <div ref={setScrollContainer} className="absolute inset-0 overflow-auto">
          {/* At least the viewport wide so columns can grow into spare width, and at
              least the columns' minimum widths so a crowded grid scrolls instead. */}
          <div className="w-full min-w-max">
            {/* Provider Header Row — sticky top so it stays during vertical scroll,
                scrolls horizontally with grid since it's inside the scroll container */}
            <div className="flex sticky top-0 z-20 border-b border-border/50 bg-background">
              {/* Time column header spacer — sticky left to stay in place during horizontal scroll */}
              <div
                className="shrink-0 bg-background border-r border-border/50 sticky left-0 z-30"
                style={{ width: TIME_COLUMN_WIDTH, height: HEADER_HEIGHT }}
              />
              {/* Column headers */}
              {columns.map(({ key, date, providerData }) => {
                const color = providerColors.get(providerData.provider.id);
                const colTz = columnTimeZone(providerData.location);
                const openSlots = providerData.slots.filter((s) => s.status === 'open');
                const openCount = openSlots.length;
                const totalOpenMinutes = openSlots.reduce((sum, s) => sum + s.durationMinutes, 0);
                const openHours = totalOpenMinutes / 60;
                const timeLabel =
                  totalOpenMinutes >= 60
                    ? `${openHours % 1 === 0 ? openHours : openHours.toFixed(1)} hrs`
                    : `${totalOpenMinutes}m`;
                const providerName = `${providerData.provider.firstName} ${providerData.provider.lastName}`;
                // Today at this column's clinic, which may not be the viewer's today.
                const today = isClinicToday(date, colTz);
                return (
                  <div
                    key={key}
                    className={cn(
                      COLUMN_WIDTH_CLASSES,
                      'px-3 py-1.5 border-r border-border/30',
                      multiDay && today
                        ? 'border-t-2 border-t-primary bg-primary/10'
                        : (color?.bg ?? 'bg-background')
                    )}
                    style={{ height: HEADER_HEIGHT }}
                  >
                    {multiDay ? (
                      // Several days: the day leads, since that's what's being chosen;
                      // provider and clinic follow on one line.
                      <>
                        <span
                          className={cn(
                            'flex items-center gap-1.5 text-sm font-medium leading-tight',
                            today && 'text-primary'
                          )}
                        >
                          <span className="truncate">{format(date, 'EEE, MMM d')}</span>
                          {today && (
                            <span className="shrink-0 rounded bg-highlight px-1 text-xs font-semibold uppercase text-highlight-foreground">
                              Today
                            </span>
                          )}
                        </span>
                        <span className="flex items-baseline gap-1 text-[10px] text-muted-foreground leading-tight">
                          <span className="min-w-0 truncate">
                            {providerName}
                            {providerData.location && ` · ${providerData.location.name}`}
                          </span>
                          <span className="shrink-0">&middot;</span>
                          <span className="shrink-0 font-medium tabular-nums">
                            {tzAbbrev(colTz)}
                          </span>
                        </span>
                      </>
                    ) : (
                      <>
                        <span className="text-sm truncate block leading-tight">{providerName}</span>
                        {providerData.location && (
                          // Name truncates; the tz badge stays pinned (shrink-0) so it's
                          // never clipped — it's what disambiguates same-row slots across
                          // timezones.
                          <span className="flex items-baseline gap-1 text-[10px] text-muted-foreground leading-tight">
                            <span className="min-w-0 truncate">{providerData.location.name}</span>
                            <span className="shrink-0">&middot;</span>
                            <span className="shrink-0 font-medium tabular-nums">
                              {tzAbbrev(colTz)}
                            </span>
                          </span>
                        )}
                      </>
                    )}
                    <span className="text-[10px] text-muted-foreground">
                      {openCount} open &middot; {timeLabel}
                    </span>
                  </div>
                );
              })}
            </div>

            {/* Grid body */}
            <div className="flex">
              {/* Time Column — sticky left so it stays during horizontal scroll */}
              <div
                className="shrink-0 bg-background border-r border-border/50 sticky left-0 z-10"
                style={{ width: TIME_COLUMN_WIDTH }}
              >
                {timeSlots.map((slot) => (
                  <div
                    key={`time-${slot.hour}-${slot.minute}`}
                    className={cn(
                      'flex items-start justify-end pr-2 text-[11px] text-muted-foreground whitespace-nowrap',
                      slot.minute === 0 && 'border-t border-border/40 font-medium'
                    )}
                    style={{ height: SLOT_HEIGHT_PX }}
                  >
                    {(slot.minute === 0 || slot.minute === 30) && (
                      <span
                        className={cn('pt-0.5', slot.minute === 30 && 'text-[10px] opacity-60')}
                      >
                        {slot.label}
                      </span>
                    )}
                  </div>
                ))}
              </div>

              {/* Provider Columns */}
              {columns.map(({ key, date, providerData }) => {
                const color = providerColors.get(providerData.provider.id);
                return (
                  <ProviderColumn
                    key={key}
                    providerData={providerData}
                    color={color}
                    startHour={startHour}
                    endHour={endHour}
                    date={date}
                    // Only among several days: in a single day every column is that day.
                    highlightToday={
                      multiDay && isClinicToday(date, columnTimeZone(providerData.location))
                    }
                    searchActive={lowerTerm.length > 0}
                    matchingSlotIds={matchingSlotIds}
                    activeSlotId={activeSlotId}
                    registerSlotRef={registerSlotRef}
                    boundary={scrollEl}
                    openCardId={openCardId}
                    onCardOpenChange={onCardOpenChange}
                    onSlotClick={onSlotClick}
                    currentAppointmentId={currentAppointmentId}
                    refusesSlot={refusesSlot}
                    preview={
                      preview &&
                      preview.providerId === providerData.provider.id &&
                      preview.locationId === (providerData.location?.id ?? null)
                        ? preview
                        : null
                    }
                  />
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

interface ProviderColumnProps {
  providerData: ProviderDaySlots;
  color: ProviderColor | undefined;
  startHour: number;
  endHour: number;
  /** The day this column shows. */
  date: Date;
  /** Tint the column: it is today, among several days. */
  highlightToday?: boolean;
  /** A patient search is active — drives highlight/dim. */
  searchActive: boolean;
  /** Ids of slots matching the active search (empty when inactive). */
  matchingSlotIds: ReadonlySet<string>;
  /** Id of the match currently focused by the prev/next controls (null when none). */
  activeSlotId: string | null;
  /** Registers a matched slot's DOM node so the parent can scroll it into view. */
  registerSlotRef: (id: string, el: HTMLDivElement | null) => void;
  /** Scroll container the hover cards are kept within (their collision boundary). */
  boundary: HTMLElement | null;
  /** Id of the slot whose hover card is currently open (null = none). */
  openCardId: string | null;
  /** Reports a card's open/close so the grid keeps a single card open. */
  onCardOpenChange: (slotId: string, open: boolean) => void;
  onSlotClick?: (slot: DaySlot, providerId: string, location: ColumnLocation) => void;
  currentAppointmentId?: string;
  refusesSlot?: (slot: DaySlot) => boolean;
  /** The preview, only when it belongs to this column. */
  preview: SlotsPreview | null;
}

function ProviderColumn({
  providerData,
  color,
  startHour,
  endHour,
  date,
  highlightToday,
  searchActive,
  matchingSlotIds,
  activeSlotId,
  registerSlotRef,
  boundary,
  openCardId,
  onCardOpenChange,
  onSlotClick,
  currentAppointmentId,
  refusesSlot,
  preview,
}: ProviderColumnProps): React.ReactElement {
  const { provider, location, slots } = providerData;
  // This column's own timezone — slots position by, and read in, their clinic's
  // local clock, independent of other columns and of the viewer.
  const displayTz = columnTimeZone(location);
  const providerName = `${provider.firstName} ${provider.lastName}`.trim();
  const dateStr = format(date, 'yyyy-MM-dd');
  const rowCount = (endHour - startHour) * (60 / SLOT_INTERVAL_MINUTES);
  const totalMinutes = (endHour - startHour) * 60;
  const pxPerMin = SLOT_HEIGHT_PX / SLOT_INTERVAL_MINUTES;
  const columnHeight = rowCount * SLOT_HEIGHT_PX;

  // Lay slots out into lanes so overlapping slots render side-by-side instead of
  // hiding each other; each block is absolutely positioned by its start + duration.
  const positioned = useMemo(() => {
    type Ev = { slot: DaySlot; startMin: number; endMin: number; lane: number; lanes: number };
    const evs: Ev[] = slots
      .map((slot) => {
        const z = toZonedTime(slot.startTime, displayTz);
        const startMin = (z.getHours() - startHour) * 60 + z.getMinutes();
        return { slot, startMin, endMin: startMin + slot.durationMinutes, lane: 0, lanes: 1 };
      })
      .filter((e) => e.endMin > 0 && e.startMin < totalMinutes) // overlaps the window
      .sort((a, b) => a.startMin - b.startMin || a.endMin - b.endMin);

    // Greedy lane assignment per overlap cluster: a cluster is a run of slots that
    // chain-overlap; within it each slot takes the first lane that's free at its start.
    let cluster: Ev[] = [];
    let clusterEnd = -Infinity;
    const flush = (): void => {
      const laneEnds: number[] = [];
      for (const e of cluster) {
        let lane = laneEnds.findIndex((end) => end <= e.startMin);
        if (lane === -1) {
          lane = laneEnds.length;
          laneEnds.push(e.endMin);
        } else {
          laneEnds[lane] = e.endMin;
        }
        e.lane = lane;
      }
      for (const e of cluster) e.lanes = laneEnds.length;
      cluster = [];
    };
    for (const e of evs) {
      if (cluster.length > 0 && e.startMin >= clusterEnd) flush();
      cluster.push(e);
      clusterEnd = Math.max(clusterEnd, e.endMin);
    }
    flush();
    return evs;
  }, [slots, displayTz, startHour, totalMinutes]);

  const now = new Date();

  // The preview block's place on this column's clock, or null when it's off-grid.
  const previewBox = useMemo(() => {
    // Only on its own day: the grid's rows are a clock, so a preview from another
    // day would otherwise land at the same hour on every day browsed to.
    if (!preview || formatInTimeZone(preview.startTime, displayTz, 'yyyy-MM-dd') !== dateStr) {
      return null;
    }
    const z = toZonedTime(preview.startTime, displayTz);
    const startMin = (z.getHours() - startHour) * 60 + z.getMinutes();
    const endMin = startMin + preview.durationMinutes;
    if (endMin <= 0 || startMin >= totalMinutes) return null;
    const picked = preview.slotId
      ? positioned.find((e) => e.slot.id === preview.slotId && e.slot.status === 'open')
      : undefined;
    const lanePct = picked ? 100 / picked.lanes : 100;
    return {
      top: Math.max(0, startMin) * pxPerMin,
      height: Math.max(
        SLOT_HEIGHT_PX,
        (Math.min(totalMinutes, endMin) - Math.max(0, startMin)) * pxPerMin
      ),
      left: `${picked ? picked.lane * lanePct : 0}%`,
      width: `${lanePct}%`,
    };
  }, [preview, positioned, displayTz, dateStr, startHour, totalMinutes, pxPerMin]);

  return (
    <div
      // Same flex sizing as the column's header, so the two stay aligned.
      className={cn(
        COLUMN_WIDTH_CLASSES,
        'relative border-r border-border/30',
        highlightToday && 'bg-primary/5'
      )}
      style={{ height: columnHeight }}
    >
      {/* Background grid — empty clickable cells (book on the fly) + gridlines. */}
      {Array.from({ length: rowCount }, (_, i) => {
        const minutes = i * SLOT_INTERVAL_MINUTES;
        const hour = startHour + Math.floor(minutes / 60);
        const minute = minutes % 60;
        // The cell's instant = its wall-clock in the display (location) timezone, so
        // booking a 9:00 cell means 9:00 at the practice, not the viewer.
        const cellStart = fromZonedTime(`${dateStr}T${pad2(hour)}:${pad2(minute)}:00`, displayTz);
        const isPast = isBefore(cellStart, now);
        return (
          <button
            key={i}
            disabled={isPast}
            className={cn(
              'absolute left-0 right-0 transition-colors',
              minute % 15 === 0 && 'border-b border-border/20',
              minute === 0 && 'border-border/40',
              isPast ? 'cursor-not-allowed opacity-40' : 'hover:bg-muted/20'
            )}
            style={{ top: i * SLOT_HEIGHT_PX, height: SLOT_HEIGHT_PX }}
            onClick={() => {
              if (isPast) return;
              const cellEnd = new Date(cellStart.getTime() + SLOT_INTERVAL_MINUTES * 60000);
              onSlotClick?.(
                {
                  id: `empty-${hour}-${minute}`,
                  startTime: cellStart.toISOString(),
                  endTime: cellEnd.toISOString(),
                  durationMinutes: SLOT_INTERVAL_MINUTES,
                  status: 'open',
                  appointmentTypeId: null,
                  appointmentTypeName: null,
                  acceptedTypeIds: null,
                  appointment: null,
                },
                provider.id,
                location
              );
            }}
          />
        );
      })}

      {/* Slot blocks, lane-positioned over the grid. */}
      {positioned.map((e) => {
        const top = Math.max(0, e.startMin) * pxPerMin;
        const height = Math.max(
          SLOT_HEIGHT_PX - 1,
          (Math.min(totalMinutes, e.endMin) - Math.max(0, e.startMin)) * pxPerMin
        );
        const widthPct = 100 / e.lanes;
        const isPast = isBefore(new Date(e.slot.startTime), now);
        // Patient search: highlight matching slots, dim the rest. The grid stays
        // whole so open slots remain bookable while a search is active.
        const highlighted = searchActive && matchingSlotIds.has(e.slot.id);
        const activeMatch = highlighted && e.slot.id === activeSlotId;
        const dimmed = searchActive && !highlighted;
        const cell = (
          <SlotCell
            slot={e.slot}
            color={color}
            isPast={isPast}
            displayTz={displayTz}
            heightPx={height}
            compact={e.lanes > 1}
            isCurrent={e.slot.appointment?.id === currentAppointmentId && !!currentAppointmentId}
            refused={refusesSlot?.(e.slot) ?? false}
            highlighted={highlighted}
            activeMatch={activeMatch}
            dimmed={dimmed}
            onClick={() => onSlotClick?.(e.slot, provider.id, location)}
          />
        );
        return (
          <div
            key={e.slot.id ?? `${e.slot.startTime}-${e.lane}`}
            // Register matched blocks so prev/next can scroll them into view.
            ref={highlighted ? (el) => registerSlotRef(e.slot.id, el) : undefined}
            // z-10 keeps slot blocks above the background cells so a click on a
            // block (or its 1px padding) never falls through to the empty-cell
            // "book a new appointment" handler underneath. Matched slots lift to
            // z-20 (active match to z-30) so their highlight ring isn't clipped by
            // neighbouring lanes.
            className={cn(
              'absolute p-px transition-opacity',
              activeMatch ? 'z-30' : highlighted ? 'z-20' : 'z-10'
            )}
            style={{ top, height, left: `${e.lane * widthPct}%`, width: `${widthPct}%` }}
          >
            {/* Only open/booked slots carry detail worth a hover card (and a valid
                button trigger). Unavailable slots render the bare cell. */}
            {e.slot.status === 'unavailable' ? (
              cell
            ) : (
              <SlotHoverCard
                slot={e.slot}
                displayTz={displayTz}
                providerName={providerName}
                locationName={location?.name ?? null}
                open={openCardId === e.slot.id}
                onOpenChange={(o) => onCardOpenChange(e.slot.id, o)}
                boundary={boundary}
              >
                {cell}
              </SlotHoverCard>
            )}
          </div>
        );
      })}

      {preview && previewBox && (
        <div
          aria-hidden
          // Position is computed at runtime from the picked time (like the slot
          // blocks above); pointer-events-none keeps the cells under it clickable.
          className="pointer-events-none absolute z-40 p-px"
          style={{
            top: previewBox.top,
            height: previewBox.height,
            left: previewBox.left,
            width: previewBox.width,
          }}
        >
          {/* Opaque base so the slot's own text doesn't show through the tint. */}
          <div className="h-full w-full overflow-hidden rounded bg-background shadow-md">
            <div className="flex h-full w-full items-center justify-center rounded border-2 border-primary bg-primary/15 px-1.5">
              <span className="truncate text-[10px] font-semibold leading-tight text-primary">
                {preview.label}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Omit the native `slot`/`color` button attributes so our richer props win.
interface SlotCellProps extends Omit<React.ComponentPropsWithoutRef<'button'>, 'slot' | 'color'> {
  slot: DaySlot;
  color: ProviderColor | undefined;
  isPast: boolean;
  displayTz: string;
  /** Pixel height of the block — drives how much detail fits (short slots show less). */
  heightPx: number;
  compact?: boolean;
  /** The booked appointment being rescheduled — marked so it reads as "this one". */
  isCurrent?: boolean;
  /** An open slot the pick can't take (only for other visit types) — drawn muted. */
  refused?: boolean;
  /** Patient-search match — lift with a ring. */
  highlighted?: boolean;
  /** The match currently focused by prev/next — stronger ring than other matches. */
  activeMatch?: boolean;
  /** Patient-search non-match — fade back so matches stand out. */
  dimmed?: boolean;
}

// forwardRef + prop spread so the cell can serve as a Radix HoverCard trigger
// (asChild) — the hover-open handlers and ref are merged onto the root element.
const SlotCell = forwardRef<HTMLButtonElement, SlotCellProps>(function SlotCell(
  {
    slot,
    color,
    isPast,
    displayTz,
    heightPx,
    compact,
    isCurrent,
    refused,
    highlighted,
    activeMatch,
    dimmed,
    className,
    onClick,
    ...rest
  },
  ref
): React.ReactElement {
  const startTime = toZonedTime(slot.startTime, displayTz);
  const timeLabel = format(startTime, 'h:mm a');
  const searchClasses = cn(
    highlighted && !activeMatch && 'ring-2 ring-amber-400 ring-offset-1 ring-offset-background',
    activeMatch && 'ring-2 ring-amber-500 ring-offset-2 ring-offset-background shadow-md',
    dimmed && 'opacity-30'
  );

  if (slot.status === 'open') {
    // Detail scales with available height: a 5-min slot (~12px) shows just a bar,
    // a 15-min shows time, taller slots add the appointment type. Hover = full info.
    const showTime = heightPx >= 28;
    const showType = heightPx >= 52 && !!slot.appointmentTypeName;
    return (
      <button
        ref={ref}
        // aria-disabled (not the native `disabled` attribute) so past slots stay
        // non-bookable yet still emit pointer/focus events — the hover card needs
        // them to open. The click is guarded below.
        aria-disabled={isPast || undefined}
        onClick={isPast ? undefined : onClick}
        className={cn(
          'h-full w-full overflow-hidden rounded border border-dashed transition-all',
          refused
            ? 'border-border bg-muted/30 hover:bg-muted/50'
            : cn(
                'border-emerald-300 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-950/30',
                'hover:border-emerald-400 hover:bg-emerald-100 dark:hover:bg-emerald-950/50'
              ),
          'focus:outline-hidden focus:ring-2 focus:ring-emerald-500/50',
          'group cursor-pointer',
          (isPast || refused) && 'cursor-not-allowed',
          searchClasses,
          className
        )}
        {...rest}
      >
        <div
          className={cn(
            'flex flex-col items-center justify-center gap-0.5 h-full px-1 text-center leading-none',
            refused ? 'text-muted-foreground' : 'text-emerald-600 dark:text-emerald-400'
          )}
        >
          <span className="text-[9px] font-semibold uppercase tracking-wide">
            {refused ? 'Other type' : 'Open'}
          </span>
          {showType && (
            <span className="line-clamp-2 text-[10px] font-medium leading-tight">
              {slot.appointmentTypeName}
            </span>
          )}
          {showTime && (
            <span className="text-[9px] opacity-70">
              {timeLabel} &middot; {slot.durationMinutes}m
            </span>
          )}
        </div>
      </button>
    );
  }

  if (slot.status === 'booked' && slot.appointment) {
    const showTime = !compact && heightPx >= 28;
    return (
      <button
        ref={ref}
        onClick={onClick}
        className={cn(
          'h-full overflow-hidden rounded border transition-all',
          'border-border bg-muted/50',
          'hover:bg-muted/70',
          'focus:outline-hidden focus:ring-2 focus:ring-primary/50',
          'cursor-pointer',
          color?.border,
          'border-l-4',
          isCurrent && 'border-dashed border-primary bg-primary/10 ring-2 ring-primary/60',
          // Always fill the lane width — the absolute lane wrapper sizes the
          // column, so an old flex-1 here (no flex parent) would collapse it.
          'w-full min-w-0',
          searchClasses,
          className
        )}
        {...rest}
      >
        <div className="flex items-center justify-center gap-1.5 h-full px-1 leading-none">
          <span
            className={cn('font-bold text-foreground/80', compact ? 'text-[10px]' : 'text-[11px]')}
          >
            {slot.appointment.patient?.initials ?? '??'}
          </span>
          {isCurrent && !compact && (
            <span className="text-[9px] font-semibold uppercase text-primary">Current</span>
          )}
          {showTime && (
            <span className="text-[9px] text-muted-foreground truncate">{timeLabel}</span>
          )}
        </div>
      </button>
    );
  }

  // Unavailable slot
  return (
    <div
      className={cn(
        'h-full w-full rounded bg-muted/40 flex items-center justify-center',
        searchClasses,
        className
      )}
    >
      <span className="text-[10px] text-muted-foreground">-</span>
    </div>
  );
});

function SlotsViewSkeleton(): React.ReactElement {
  return (
    <div className="flex flex-col h-full">
      {/* Summary Bar Skeleton */}
      <div className="shrink-0 px-4 py-2 border-b border-border/50 bg-muted/30">
        <Skeleton className="h-5 w-64" />
      </div>

      {/* Header + Grid Skeleton */}
      <div className="flex-1 min-h-0 overflow-hidden">
        <div className="flex">
          {/* Time Column */}
          <div className="shrink-0" style={{ width: TIME_COLUMN_WIDTH }}>
            <div style={{ height: HEADER_HEIGHT }} />
            {[...Array(16)].map((_, i) => (
              <Skeleton key={i} className="h-9 w-full mb-0.5" />
            ))}
          </div>
          {/* Provider Columns */}
          {[...Array(6)].map((_, i) => (
            <div key={i} className="shrink-0" style={{ width: PROVIDER_COLUMN_WIDTH }}>
              <Skeleton className="h-14 w-full mb-0.5" />
              {[...Array(16)].map((_, j) => (
                <Skeleton key={j} className="h-9 w-full mb-0.5" />
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
